import type { MouseEvent } from 'react';
import {
  Button,
  Menu,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  type ButtonProps,
  type MenuProps,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular, MoreHorizontalFilled } from '@fluentui/react-icons';
import type { TAction, TItem } from '@modules/items/types/items.types';
import { ControlledTooltip } from './ControlledTooltip';
import { MenuItemLoading } from './loading/MenuItemLoading';
import { TableActionButtons } from './tables/TableActionButtons';

const TRIGGERS = {
  card: { appearance: 'subtle', size: 'medium', icon: <MoreHorizontal20Regular /> },
  row: { appearance: 'secondary', icon: <MoreHorizontalFilled /> },
} satisfies Record<string, Pick<ButtonProps, 'appearance' | 'size' | 'icon'>>;

const useStyles = makeStyles({
  // A larger hit area than the icon needs, laid out in the space of a small button.
  cardTrigger: {
    margin: '-4px',
  },
});

type TProps = {
  item: TItem;
  actions: TAction[];
  variant: keyof typeof TRIGGERS;
  triggerClassName?: string;
  open?: MenuProps['open'];
  onOpenChange?: MenuProps['onOpenChange'];
};

// Cards sit inside a router Link, and clicks in the popover bubble to it through the portal.
const stop = (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

export const ItemActionsMenu = ({ item, actions, variant, triggerClassName, open, onOpenChange }: TProps) => {
  const classes = useStyles();

  return (
    <Menu open={open} onOpenChange={onOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <Button
          {...TRIGGERS[variant]}
          aria-label="More actions"
          className={mergeClasses(variant === 'card' && classes.cardTrigger, triggerClassName)}
          onClick={stop}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList onClick={stop}>
          {actions
            .filter((action) => action.show([item]))
            .map((action) => {
              const disabledMessage = action.getDisabledMessage?.([item]) ?? '';
              const enable = action.enable([item]);
              return (
                <ControlledTooltip
                  key={action.children}
                  enable={enable && Boolean(disabledMessage)}
                  content={disabledMessage}
                  relationship="inaccessible"
                >
                  <MenuItemLoading
                    icon={action.icon}
                    disabled={!enable || Boolean(disabledMessage)}
                    onClick={async () => await action.onClick([item]) /* must be await */}
                  >
                    {action.children}
                  </MenuItemLoading>
                </ControlledTooltip>
              );
            })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

type TRowProps = Omit<TProps, 'variant'> & {
  alwaysVisible?: boolean;
};

// The row menu of every item table.
export const ItemRowActionsMenu = ({ alwaysVisible, ...props }: TRowProps) => (
  <TableActionButtons alwaysVisible={alwaysVisible}>
    <ItemActionsMenu {...props} variant="row" />
  </TableActionButtons>
);
